import { DeviceMode, type NodeV2 } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { saveArea } from 'components/Editor/NodeV2/formControls/Area/utils'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { ABSOLUTE_PORTAL_GAP } from 'components/Editor/components/AbsolutePortal/constants'
import KeyBindTag from 'components/Editor/components/KeyBindTag/KeyBindTag'
import Button, { IconButton } from 'components/uiKit/Button'
import DropDown, { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { updateBlock } from 'services/Store/Project/actions'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { addGridRows, addNode, removeGridRows } from 'services/Store/Project/mutators/node'
import {
  getIsActiveBlock,
  getIsActiveNode,
  getIsDestination,
  getIsEditingGrid,
  getIsShowGrid,
  getIsTarget,
  getNodeAnimationState,
  getNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block, IBlockMode, MapType } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import Picker from '../../Picker'
import { onEditGrid } from '../../commands'
import { safeParseGrid } from '../../formControls/Grid/utils'
import { isRoot as checkIsRoot } from '../../guards'
import { useContentPlus } from '../../useContentPlus'
import { findCol, findRow } from '../utils'
import * as s from './Grid.module.scss'
import GridMesh from './GridMesh'

const DELAY = 500

interface IGridControlsProps {
  mode: IBlockMode
  block: Block
  node: NodeV2
}

const GridControls: React.FC<IGridControlsProps> = ({ block, mode, node }) => {
  const { deviceMode, editorMode } = mode
  const hoverTimeout = useRef<NodeJS.Timeout>()
  const dispatch = useProjectDispatch()
  const isPro = editorMode === AppMode.pro
  const isDesktop = deviceMode === DeviceMode.desktop
  const style = useProjectContext(getNodeStyle, block.uuid, node.id)
  const [showPicker, setShowPicker] = useState(false)
  const [hoveredCol, setHoveredCol] = useState<number | null>(null)
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)
  const cellArea = `${hoveredRow} / ${hoveredCol} / ${(hoveredRow || 1) + 1} / ${(hoveredCol || 1) + 1}`
  const isActiveBlock = useProjectContext(getIsActiveBlock, block.uuid)
  const isAnimation = useProjectContext(getNodeAnimationState, node.id)
  const currentTarget = useProjectContext(getIsTarget, node.id)
  const showMesh = !isAnimation && !currentTarget && isActiveBlock
  const isEditingGrid = useProjectContext(getIsEditingGrid, node.id)
  const isActive = useProjectContext(getIsActiveNode, node.id)
  const isShowGrid = useProjectContext(getIsShowGrid, block.uuid, node.id)
  const isDestination = useProjectContext(getIsDestination, node.id)
  const showPlus = isDesktop && hoveredRow && hoveredCol && hoveredRow !== -1 && hoveredCol !== -1
  const rows = useMemo(() => safeParseGrid(style?.gridRows).items, [style?.gridRows])
  const isRoot = checkIsRoot(block, node)
  const isContentPlus = useContentPlus()

  const onChange = useCallback(
    (mutator: (block: Block) => Block) =>
      dispatch(updateBlock({ id: block?.uuid, value: mutator(block) })),
    [block, dispatch],
  )

  const handleAddChild = useCallback(
    (newNode: NodeV2, data?: { elements?: MapType<EditorElement> }) => {
      const cols = safeParseGrid(style?.gridColumns).items.length
      const colSize = R.clamp(2, Math.max(2, cols), Math.floor((cols - 2) / 2))
      onChange(
        addNode(
          node.id,
          R.assocPath<string, NodeV2>(
            ['style', deviceMode, 'gridArea'],
            saveArea({
              rowStart: hoveredRow || 1,
              colStart: hoveredCol || 1,
              rowEnd: (hoveredRow || 1) + 1,
              colEnd: (hoveredCol || 1) + colSize,
            }),
            newNode,
          ),
          data,
        ),
      )
      setShowPicker(false)
    },
    [onChange, hoveredRow, hoveredCol, deviceMode, node, style],
  )

  const handleRemoveRow = useCallback(
    () => onChange(removeGridRows(node.id, deviceMode)),
    [deviceMode, node.id, onChange],
  )

  const handleAddRow = useCallback(
    () => onChange(addGridRows(node.id, deviceMode)),
    [deviceMode, node.id, onChange],
  )
  const handleRemoveFourRows = useCallback(
    () => onChange(removeGridRows(node.id, deviceMode, 4)),
    [deviceMode, node.id, onChange],
  )

  const handleAddFourRows = useCallback(
    () => onChange(addGridRows(node.id, deviceMode, 4)),
    [deviceMode, node.id, onChange],
  )
  const handleRemoveEightRows = useCallback(
    () => onChange(removeGridRows(node.id, deviceMode, 8)),
    [deviceMode, node.id, onChange],
  )

  const handleAddEightRows = useCallback(
    () => onChange(addGridRows(node.id, deviceMode, 8)),
    [deviceMode, node.id, onChange],
  )

  const hideControls = useCallback(() => {
    if (!showPicker) {
      hoverTimeout.current = setTimeout(() => {
        setHoveredCol(null)
        setHoveredRow(null)
      }, DELAY)
    }
  }, [showPicker])

  useEffect(() => {
    let animationFrameId: number

    const handleMouseMove = (event: MouseEvent) => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }

      animationFrameId = requestAnimationFrame(() => {
        if (isActiveBlock) {
          const row = findRow({ x: event.clientX, y: event.clientY }, node.id /*, true */)
          const col = findCol({ x: event.clientX, y: event.clientY }, node.id /*, true */)

          if (!showPicker && row) {
            clearTimeout(hoverTimeout.current)
            setHoveredRow(row)
          }
          if (!showPicker && col) {
            clearTimeout(hoverTimeout.current)
            setHoveredCol(col)
          }
        }
      })
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseout', hideControls)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseout', hideControls)

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [hideControls, node.id, showPicker, isActiveBlock])

  return (
    <>
      {(isActive || isDestination || isShowGrid) && isEditingGrid && (
        <AbsolutePortal
          chain='vertical'
          name='zIndex'
          offset={`${-ABSOLUTE_PORTAL_GAP}px`}
          placement='leftTop'
          translateX='-100%'
          translateY='-100%'
        >
          <Tooltip
            mouseEnterDelay={0.5}
            overlay={
              <div className={s.label}>
                {t('editorV2.grid.endEdit')}
                <KeyBindTag bind='ctrl+E' dark />
              </div>
            }
          >
            <div className={s.close}>
              <Icon name='otherClose' onClick={onEditGrid} size={KitSize.XS} />
            </div>
          </Tooltip>
        </AbsolutePortal>
      )}
      {(isActive || isDestination || isShowGrid) && (isPro || isContentPlus) && showPlus && (
        <div className={s.add} style={{ gridArea: cellArea }}>
          <AbsolutePortal name='add'>
            <div className={s.cell}>
              <DropDown
                onVisibleChange={setShowPicker}
                overlay={<Picker block={block} mode={mode} node={node} onAdd={handleAddChild} />}
                placement='right-start'
                styleType='clear'
                visible={showPicker}
              >
                <IconButton
                  background='var(--color-grid)'
                  icon='add'
                  name='addChild'
                  size={KitSize.XS}
                  round
                />
              </DropDown>
            </div>
          </AbsolutePortal>
        </div>
      )}
      {(isActive || isDestination || isShowGrid) && showMesh && (
        <GridMesh block={block} mode={mode} node={node} />
      )}
      {(isPro || isContentPlus) && isActiveBlock && isRoot && (
        <AbsolutePortal
          chain='vertical'
          name='zIndex'
          offset={`${ABSOLUTE_PORTAL_GAP}px 0`}
          placement='bottomLeft'
        >
          <div className={s.bottomActions}>
            <Tooltip
              mouseEnterDelay={0.5}
              overlay={t('editorV2.grid.removeRows', { count: 8 })}
              placement='top'
            >
              <Button
                background='var(--color-grid)'
                disabled={rows.length <= 8}
                name='decrement'
                onClick={handleRemoveEightRows}
                size={KitSize.S}
              >
                <Icon name='minusThin' size={KitSize.S} /> 8
              </Button>
            </Tooltip>
            <Tooltip
              mouseEnterDelay={0.5}
              overlay={t('editorV2.grid.removeRows', { count: 4 })}
              placement='top'
            >
              <Button
                background='var(--color-grid)'
                disabled={rows.length <= 4}
                name='decrement'
                onClick={handleRemoveFourRows}
                size={KitSize.S}
              >
                <Icon name='minusThin' size={KitSize.S} /> 4
              </Button>
            </Tooltip>
            <Tooltip
              mouseEnterDelay={0.5}
              overlay={t('editorV2.grid.removeRows', { count: 1 })}
              placement='top'
            >
              <IconButton
                background='var(--color-grid)'
                disabled={rows.length <= 1}
                icon='minusThin'
                name='minus'
                onClick={handleRemoveRow}
                size={KitSize.S}
              />
            </Tooltip>
            <Tooltip
              mouseEnterDelay={0.5}
              overlay={t('editorV2.grid.addRows', { count: 1 })}
              placement='top'
            >
              <IconButton
                background='var(--color-grid)'
                icon='otherAdd'
                name='plus'
                onClick={handleAddRow}
                size={KitSize.S}
              />
            </Tooltip>
            <Tooltip
              mouseEnterDelay={0.5}
              overlay={t('editorV2.grid.addRows', { count: 4 })}
              placement='top'
            >
              <Button
                background='var(--color-grid)'
                name='decrement'
                onClick={handleAddFourRows}
                size={KitSize.S}
              >
                <Icon name='otherAdd' size={KitSize.S} /> 4
              </Button>
            </Tooltip>
            <Tooltip
              mouseEnterDelay={0.5}
              overlay={t('editorV2.grid.addRows', { count: 8 })}
              placement='top'
            >
              <Button
                background='var(--color-grid)'
                name='decrement'
                onClick={handleAddEightRows}
                size={KitSize.S}
              >
                <Icon name='otherAdd' size={KitSize.S} /> 8
              </Button>
            </Tooltip>
          </div>
        </AbsolutePortal>
      )}
    </>
  )
}

export default React.memo(GridControls)
